/*
 * Treat — gallery pagination dashes.
 *
 * Sense's clickable pagination (.slider-counter__link) is wired only in SlideshowComponent
 * (global.js:843). The product gallery uses the plain SliderComponent, which renders a "1 / 5"
 * text counter and has no clickable pagination at all. Rather than edit global.js — shared with
 * every other section — this element drives the existing SliderComponent through its public
 * surface only:
 *
 *   read   slider.totalPages, slider.sliderItemOffset, slider.currentPage
 *   call   slider.setSlidePosition(px)          (global.js:820)
 *   listen 'slideChanged' CustomEvent            (global.js:781, detail.currentPage)
 *
 * Nothing here reaches into SliderComponent's internals or patches it.
 *
 * totalPages is computed in initPages(), which SliderComponent runs in its constructor AND again
 * from a ResizeObserver. It can therefore be undefined or stale at connect time, so the dashes are
 * rebuilt on the first slideChanged and on every resize rather than being counted once at init.
 */
if (!customElements.get('treat-gallery-dots')) {
  customElements.define(
    'treat-gallery-dots',
    class TreatGalleryDots extends HTMLElement {
      connectedCallback() {
        this.slider = document.getElementById(this.dataset.sliderId);
        if (!this.slider) return;

        this.labelTemplate = this.dataset.dotLabel || 'Go to item %INDEX%';
        this.onSlideChanged = this.onSlideChanged.bind(this);
        this.slider.addEventListener('slideChanged', this.onSlideChanged);

        this.resizeObserver = new ResizeObserver(() => this.render());
        this.resizeObserver.observe(this.slider);

        this.render();
      }

      disconnectedCallback() {
        if (this.slider) this.slider.removeEventListener('slideChanged', this.onSlideChanged);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      onSlideChanged(event) {
        this.render(event.detail && event.detail.currentPage);
      }

      render(currentPage) {
        const total = this.slider.totalPages;
        // Fewer than two pages means everything is already visible — nothing to indicate.
        if (!total || total < 2) {
          this.replaceChildren();
          return;
        }

        const current = currentPage || this.slider.currentPage || 1;

        if (this.children.length !== total) {
          const dots = [];
          for (let i = 1; i <= total; i += 1) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'treat-product__dot';
            dot.setAttribute('aria-label', this.labelTemplate.replace('%INDEX%', i));
            dot.addEventListener('click', () => this.goTo(i));
            dots.push(dot);
          }
          this.replaceChildren(...dots);
        }

        Array.from(this.children).forEach((dot, index) => {
          dot.setAttribute('aria-current', index + 1 === current ? 'true' : 'false');
        });
      }

      goTo(page) {
        const offset = this.slider.sliderItemOffset;
        if (!offset) return;
        this.slider.setSlidePosition(offset * (page - 1));
      }
    }
  );
}
